import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Workflow } from '../../store/project/projectSlice';
import {
  DagSummary, RunInfo, fetchRuns, fetchSummary,
  fmtSchedule, fmtTime, fmtDuration, stateLabel, stateStyle, dagIdOf,
} from '../../services/executionApi';
import { useTranslation } from 'react-i18next';
import { formatYearMonth, weekdayNames } from '../../utils/formatters';

interface ScheduleCalendarProps {
  selectedWorkflow: Workflow | null;
  /** 실행이 끝나면 이 값이 바뀌어 다시 읽는다 */
  reloadKey?: number;
}

// 요일·연월 이름은 현재 언어로 만든다(formatters) — 한국어를 박아 두면 언어를 바꿔도 남는다

const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** 그 달을 주 단위로 — 앞뒤는 이웃 달 날짜로 채운다 */
function weeksOf(year: number, month: number): Array<Array<{ date: Date; inMonth: boolean }>> {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: Array<{ date: Date; inMonth: boolean }> = [];
  for (let i = first.getDay(); i > 0; i--) cells.push({ date: new Date(year, month, 1 - i), inMonth: false });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d), inMonth: true });
  for (let extra = 1; cells.length % 7 !== 0; extra++) cells.push({ date: new Date(year, month, daysInMonth + extra), inMonth: false });
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/**
 * 실행 일정 — 스케줄(배포된 DAG 기준)·다음 실행 예정과, 실제 실행이 있었던 날을 달력에 표시한다.
 *
 * **조회 전용이다.** 스케줄을 바꾸려면 디자이너 탭의 워크플로 설정에서 고치고 다시 배포해야 한다.
 */
const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({ selectedWorkflow, reloadKey }) => {
  const { t } = useTranslation();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [runs, setRuns] = useState<RunInfo[]>([]);
  const [info, setInfo] = useState<DagSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pickedDay, setPickedDay] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!selectedWorkflow) { setRuns([]); setInfo(null); return; }
    setLoading(true); setError('');
    try {
      const [r, s] = await Promise.all([
        fetchRuns(selectedWorkflow.id, 100),
        fetchSummary([selectedWorkflow.id]),
      ]);
      setRuns(r);
      setInfo(s[dagIdOf(selectedWorkflow.id)] || null);
    } catch (e) {
      setError((e as Error).message);
      setRuns([]); setInfo(null);
    } finally {
      setLoading(false);
    }
  }, [selectedWorkflow]);

  useEffect(() => { setPickedDay(null); load(); }, [load, reloadKey]);

  /** 날짜별 실행 묶음 (최근 100건 기준) */
  const byDay = useMemo(() => {
    const m: Record<string, RunInfo[]> = {};
    runs.forEach((r) => {
      const when = r.start_date || r.execution_date;
      if (!when) return;
      const d = new Date(when);
      if (Number.isNaN(d.getTime())) return;
      (m[dayKey(d)] ||= []).push(r);
    });
    return m;
  }, [runs]);

  const weeks = useMemo(() => weeksOf(year, month), [year, month]);
  const move = (delta: number) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth());
  };

  const nextRunKey = info?.next_dagrun && !info.is_paused ? dayKey(new Date(info.next_dagrun)) : null;
  const picked = pickedDay ? byDay[pickedDay] || [] : [];

  if (!selectedWorkflow) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 font-medium">{t('execution:cal.title')}</div>
        <div className="p-6 text-center text-gray-500">{t('execution:cal.selectWorkflow')}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex items-center gap-2">
        <span className="font-medium truncate">{t('execution:cal.workflowTitle', { name: selectedWorkflow.name })}</span>
        <button
          className="ml-auto px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          onClick={load} disabled={loading}
        >
          {loading ? t('execution:cal.reading') : t('execution:cal.refresh')}
        </button>
      </div>

      {error && <div className="px-4 py-2 text-xs text-red-700 bg-red-50">{t('execution:cal.loadFailed', { message: error })}</div>}

      {/* 스케줄 요약 — 디자이너 설정이 배포된 DAG 에 반영된 값 */}
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <div>
          <span className="text-gray-500 mr-1">{t('execution:cal.schedule')}</span>
          <span className="font-medium text-gray-800">{info?.deployed ? fmtSchedule(info.schedule_interval) : t('execution:cal.undeployed')}</span>
          {info?.is_paused && <span className="ml-1 text-xs text-amber-700">{t('execution:cal.paused')}</span>}
        </div>
        <div>
          <span className="text-gray-500 mr-1">{t('execution:cal.nextRun')}</span>
          <span className="font-medium text-gray-800">
            {info?.deployed && info.next_dagrun && !info.is_paused ? fmtTime(info.next_dagrun) : '-'}
          </span>
        </div>
        <div className="text-xs text-gray-400 self-center">
          {t('execution:cal.changeHint')}
        </div>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 달력 — 실행이 있었던 날 표시 */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <button className="px-2 py-0.5 text-sm rounded hover:bg-gray-100 text-gray-600" onClick={() => move(-1)}>‹</button>
            <h3 className="font-semibold text-sm">{formatYearMonth(year, month)}</h3>
            <button className="px-2 py-0.5 text-sm rounded hover:bg-gray-100 text-gray-600" onClick={() => move(1)}>›</button>
          </div>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {weekdayNames().map((d, i) => (
                  <th key={i} className="text-center p-1 text-xs font-medium text-gray-500">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wi) => (
                <tr key={wi}>
                  {week.map(({ date, inMonth }) => {
                    const key = dayKey(date);
                    const dayRuns = byDay[key] || [];
                    const failed = dayRuns.some((r) => r.state === 'failed');
                    const isPicked = pickedDay === key;
                    const isToday = key === dayKey(today);
                    const isNext = key === nextRunKey;
                    return (
                      <td key={key} className="text-center p-0.5">
                        <button
                          type="button"
                          onClick={() => setPickedDay(dayRuns.length ? key : null)}
                          className={`w-9 h-9 rounded-full text-xs relative ${
                            isPicked ? 'bg-blue-500 text-white'
                              : dayRuns.length ? (failed ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800')
                                : inMonth ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300'
                          } ${isToday && !isPicked ? 'ring-1 ring-blue-400' : ''}`}
                          title={dayRuns.length ? `${t('execution:cal.dayRuns', { n: dayRuns.length })}${failed ? t('execution:cal.withFailures') : ''}` : isNext ? t('execution:cal.nextPlanned') : ''}
                        >
                          {date.getDate()}
                          {isNext && <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500" />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 flex flex-wrap gap-3 text-[0.6875rem] text-gray-500">
            <span><span className="inline-block w-3 h-3 rounded-full bg-green-100 align-middle mr-1" />{t('execution:cal.allSucceeded')}</span>
            <span><span className="inline-block w-3 h-3 rounded-full bg-red-100 align-middle mr-1" />{t('execution:cal.hasFailures')}</span>
            <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 align-middle mr-1" />{t('execution:cal.nextPlanned')}</span>
            <span className="text-gray-400">{t('execution:cal.last100')}</span>
          </div>
        </div>

        {/* 고른 날짜의 실행 */}
        <div>
          <div className="text-sm font-semibold text-gray-700 mb-2">
            {pickedDay ? t('execution:cal.pickedDay', { day: pickedDay, n: picked.length }) : t('execution:cal.pickDay')}
          </div>
          {pickedDay && (
            <div className="max-h-64 overflow-auto divide-y divide-gray-100 border border-gray-100 rounded">
              {picked.map((r) => (
                <div key={r.dag_run_id} className="px-2 py-1.5 flex items-center gap-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded-full font-semibold ${stateStyle(r.state)}`}>{stateLabel(r.state)}</span>
                  <span className="text-gray-700">{fmtTime(r.start_date || r.execution_date).slice(11)}</span>
                  <span className="text-gray-400">{r.run_type === 'manual' ? t('execution:cal.manual') : r.run_type === 'scheduled' ? t('execution:cal.scheduled') : ''}</span>
                  <span className="ml-auto text-gray-400">{fmtDuration(r.start_date, r.end_date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleCalendar;
